import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';
import SEO from '../../components/SEO';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Roboto', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background-color: #f8f9fa;
  padding: 5rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 600px;
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const QuickBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(139, 110, 180, 0.1);
  color: var(--purple);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const Heading = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #2D3142;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
  
  span {
    color: var(--purple);
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #6C757D;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--purple);
  color: white;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--purple-dark);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(139, 110, 180, 0.4);
  }
`;

const DiscountTag = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #FFAA8A;
  color: #2D3142;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transform: rotate(5deg);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const CountdownContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const CountdownItem = styled.div`
  background-color: var(--purple);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  .number {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
  }
  
  .label {
    font-size: 0.7rem;
    text-transform: uppercase;
    margin-top: 0.25rem;
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
`;

const StepsSection = styled.section`
  padding: 5rem 0;
  background-color: white;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
  text-align: center;
  
  span {
    color: var(--purple);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6C757D;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const StepCard = styled.div`
  background-color: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
  
  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    border-color: var(--purple);
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: -15px;
  left: -15px;
  width: 40px;
  height: 40px;
  background-color: var(--purple);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  box-shadow: 0 5px 15px rgba(139, 110, 180, 0.3);
`;

const StepTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
`;

const StepDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
`;

const StepTime = styled.div`
  display: inline-block;
  background-color: rgba(139, 110, 180, 0.1);
  color: var(--purple);
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.8rem;
  margin-top: 1rem;
`;

const ResultsSection = styled.section`
  padding: 5rem 0;
  background-color: #f8f9fa;
`;

const ResultsCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  height: 100%;
`;

const ResultNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--purple);
  margin-bottom: 1rem;
`;

const ResultTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
`;

const ResultDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
`;

const FeaturesSection = styled.section`
  padding: 5rem 0;
  background-color: white;
`;

const FeatureCard = styled.div`
  display: flex;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background-color: rgba(139, 110, 180, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  
  i {
    font-size: 1.5rem;
    color: var(--purple);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    margin-right: 0;
  }
`;

const FeatureContent = styled.div``;

const FeatureTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #2D3142;
`;

const FeatureDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 5rem 0;
  background-color: #2D3142;
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  span {
    color: var(--purple);
  }
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--purple);
  color: white;
  font-weight: 600;
  padding: 1rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--purple-dark);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(139, 110, 180, 0.4);
  }
`;

const LandingPageQuick = () => {
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Quick Baby Sleep Solutions | Personalized Sleep Plans",
    description: "Get personalized sleep guidance with our customized sleep plan. Our approach is designed to help improve your baby's sleep patterns based on their unique needs.",
    keywords: [
      "quick baby sleep solutions", 
      "fast sleep training", 
      "baby sleep plan",
      "baby sleep results",
      "quick sleep improvement",
      "sleep training",
      "baby sleep help",
      "personalized sleep plan"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Dozzy Quick Sleep Solution",
      "description": "Get your baby sleeping better with our personalized approach to sleep improvement",
      "offers": {
        "@type": "Offer",
        "price": "49.00",
        "priceCurrency": "AUD",
        "availability": "https://schema.org/InStock",
        "priceValidUntil": "2023-12-31"
      }
    }
  };

  // Google Analytics tracking code setup
  useEffect(() => {
    // Initialize Google Analytics with the correct tracking ID
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-CTM5672S7L';
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-CTM5672S7L', {
      page_title: 'Quick Results Landing Page',
      page_path: '/landing-quick',
      campaign_source: 'facebook',
      campaign_medium: 'social',
      campaign_name: 'quick_results'
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      gtag('event', 'cta_click', {
        'event_category': 'engagement',
        'event_label': 'Quick Results Landing Page CTA',
        'value': 1
      });
    };

    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    // Countdown timer
    const countdownTimer = () => {
      const countdownElements = {
        hours: document.getElementById('countdown-hours'),
        minutes: document.getElementById('countdown-minutes'),
        seconds: document.getElementById('countdown-seconds')
      };
      
      if (countdownElements.hours && countdownElements.minutes && countdownElements.seconds) {
        let hours = 23;
        let minutes = 59;
        let seconds = 59;
        
        const interval = setInterval(() => {
          seconds--;
          
          if (seconds < 0) {
            seconds = 59;
            minutes--;
          }
          
          if (minutes < 0) {
            minutes = 59;
            hours--;
          }
          
          if (hours < 0) {
            clearInterval(interval);
            hours = 0;
            minutes = 0;
            seconds = 0;
          }
          
          countdownElements.hours.textContent = hours.toString().padStart(2, '0');
          countdownElements.minutes.textContent = minutes.toString().padStart(2, '0');
          countdownElements.seconds.textContent = seconds.toString().padStart(2, '0');
        }, 1000);
        
        return () => clearInterval(interval);
      }
    };
    
    const timerId = setTimeout(countdownTimer, 1000);
    
    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
      clearTimeout(timerId);
    };
  }, []);

  return (
    <LandingContainer>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/landing-quick"
        ogType="website"
      />
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <HeroContent>
                <QuickBadge>
                  <i className="bi bi-lightning-charge-fill me-2"></i>
                  Sleep Solutions with Artificial Intelligence (AI)
                </QuickBadge>
                <Heading>
                  Better Sleep <span>Support</span> for Your Family
                </Heading>
                <SubHeading>
                  Get your personalized sleep plan and start implementing better sleep habits for your family.
                </SubHeading>
                <CountdownContainer>
                  <CountdownItem>
                    <span className="number" id="countdown-hours">23</span>
                    <span className="label">Hours</span>
                  </CountdownItem>
                  <CountdownItem>
                    <span className="number" id="countdown-minutes">59</span>
                    <span className="label">Minutes</span>
                  </CountdownItem>
                  <CountdownItem>
                    <span className="number" id="countdown-seconds">59</span>
                    <span className="label">Seconds</span>
                  </CountdownItem>
                </CountdownContainer>
                <ButtonWrapper>
                  <CtaButton to="/signup" className="cta-button">
                    Get Your Sleep Plan Now
                  </CtaButton>
                </ButtonWrapper>
                
                <div className="mt-4">
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="mb-3">
                      <div className="d-flex align-items-center">
                        <img src="/images/logo-rednose.png" alt="Red Nose Guidelines" height="30" className="me-2" />
                        <img src="/images/logo-triple-p.png" alt="Triple P Resources" height="30" className="me-2" />
                        <img src="/images/logo-raisingchildren.png" alt="Raising Children Network" height="30" className="me-2" />
                        <img src="/images/logo-tresilian.png" alt="Tresillian" height="30" />
                      </div>
                      <p className="mb-0 mt-1" style={{ color: '#6C757D' }}>
                        <small>Evidence-based guidelines</small>
                      </p>
                    </div>
                  </div>
                </div>
              </HeroContent>
            </div>
            <div className="col-lg-6">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/kelly-sikkema-t53nC7J6Y0E-unsplash.jpg" 
                  alt="Peaceful sleeping baby" 
                />
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <StepsSection>
        <div className="container">
          <SectionTitle>
            <span>3 Simple Steps</span> to Better Sleep
          </SectionTitle>
          <SectionSubtitle>
            Our streamlined process helps guide you toward better sleep habits
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-4">
              <StepCard>
                <StepNumber>1</StepNumber>
                <StepTitle>Complete Quick Questionnaire</StepTitle>
                <StepDescription>
                  Answer a few simple questions about your child's sleep patterns and your parenting preferences.
                </StepDescription>
                <StepTime>Takes just 5 minutes</StepTime>
              </StepCard>
            </div>
            
            <div className="col-md-4">
              <StepCard>
                <StepNumber>2</StepNumber>
                <StepTitle>Receive Instant Sleep Plan</StepTitle>
                <StepDescription>
                  Get your personalized sleep plan delivered immediately to your inbox—no waiting for consultations.
                </StepDescription>
                <StepTime>Instant delivery</StepTime>
              </StepCard>
            </div>
            
            <div className="col-md-4">
              <StepCard>
                <StepNumber>3</StepNumber>
                <StepTitle>Implement & See Progress</StepTitle>
                <StepDescription>
                  Follow the simple, step-by-step instructions and watch your child's sleep habits improve with consistent implementation.
                </StepDescription>
                <StepTime>Personalized guidance for your child</StepTime>
              </StepCard>
            </div>
          </div>
        </div>
      </StepsSection>

      <ResultsSection>
        <div className="container">
          <SectionTitle>
            <span>Personalized Results</span> for Your Child
          </SectionTitle>
          <SectionSubtitle>
            Our sleep plans are tailored to your child's unique needs
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-4">
              <ResultsCard>
                <ResultNumber>Personalized</ResultNumber>
                <ResultTitle>Customized Approach</ResultTitle>
                <ResultDescription>
                  Every sleep plan is customized to your child's unique temperament, age, and sleep challenges for a more effective approach.
                </ResultDescription>
              </ResultsCard>
            </div>
            
            <div className="col-md-4">
              <ResultsCard>
                <ResultNumber>Many</ResultNumber>
                <ResultTitle>Positive Feedback</ResultTitle>
                <ResultDescription>
                  Many parents report positive changes in their child's sleep habits when following our personalized sleep plans.
                </ResultDescription>
              </ResultsCard>
            </div>
            
            <div className="col-md-4">
              <ResultsCard>
                <ResultNumber>Reported</ResultNumber>
                <ResultTitle>Potential Sleep Improvement</ResultTitle>
                <ResultDescription>
                  Some parents report gaining additional hours of sleep after implementing our plans, though individual results may vary significantly.
                </ResultDescription>
              </ResultsCard>
            </div>
          </div>
        </div>
      </ResultsSection>

      <FeaturesSection>
        <div className="container">
          <SectionTitle>
            Designed for <span>Busy Parents</span>
          </SectionTitle>
          <SectionSubtitle>
            Our sleep plans fit seamlessly into your hectic schedule
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-lg-6">
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-clock"></i>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Time-Saving Implementation</FeatureTitle>
                  <FeatureDescription>
                    Clear, concise instructions that don't require hours of reading or research. Our step-by-step format means you can start implementing tonight.
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-phone"></i>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Mobile-Friendly Format</FeatureTitle>
                  <FeatureDescription>
                    Access your sleep plan on any device, anytime. Perfect for quick reference during those middle-of-the-night moments when you need guidance.
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-calendar-check"></i>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Ready-Made Schedules</FeatureTitle>
                  <FeatureDescription>
                    No need to create complex schedules from scratch. We provide complete day-by-day plans that you can implement immediately.
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>
            </div>
            
            <div className="col-lg-6">
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-list-check"></i>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Simple Checklists</FeatureTitle>
                  <FeatureDescription>
                    Easy-to-follow checklists for bedtime routines, nap preparation, and troubleshooting common issues—perfect for parents who need clarity, not complexity.
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-arrow-repeat"></i>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Quick Troubleshooting</FeatureTitle>
                  <FeatureDescription>
                    Encounter a challenge? Our plans include rapid-response troubleshooting guides so you can quickly address issues without derailing progress.
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-graph-up"></i>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Progress Tracking</FeatureTitle>
                  <FeatureDescription>
                    Simple tracking tools help you see improvements quickly, keeping you motivated as your child's sleep gets better night after night.
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>
            </div>
          </div>
        </div>
      </FeaturesSection>

      <CTASection>
        <div className="container">
          <CTATitle style={{ color: 'white' }}>
            Better Sleep Plan Just <span>Minutes Away</span>
          </CTATitle>
          <CTADescription>
            Work toward better sleep habits. Get your personalized sleep plan now and start implementing better sleep practices for your family.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/signup" className="cta-button">
              Get Instant Access
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default LandingPageQuick;
